/**
 * OptionList — the popover a PanelDropdown or PanelStepper opens. Shared so the two controls can
 * never drift apart visually, and so click-away/list behaviour is written once.
 *
 * Opens UPWARD: panels sit at the bottom of the screen (or vertically centred), and a list
 * dropping down would leave the viewport.
 */

import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { POPOVER_SURFACE, ACCENT_TEXT, MOTION } from './tokens';

export interface OptionItem<T> {
  value: T;
  label: string;
}

interface OptionListProps<T> {
  options: readonly OptionItem<T>[];
  value: T | null;
  onSelect: (value: T) => void;
  /** Per-item delete affordance. Hidden when only one option remains. */
  onDelete?: (value: T) => void;
  /** Extra row pinned to the bottom of the list (e.g. "New World"). */
  action?: { label: string; icon?: ReactNode; onClick: () => void };
  /** Called after a pick or the action — the owner closes the popover. */
  onClose: () => void;
}

const ROW_H = 'h-10 md:h-9';

export function OptionList<T extends string | number>({
  options, value, onSelect, onDelete, action, onClose,
}: OptionListProps<T>) {
  return (
    <div className={`absolute bottom-full left-0 right-0 mb-2 z-10 ${POPOVER_SURFACE} max-h-[45vh] overflow-y-auto`}>
      {options.map((o) => (
        <div key={String(o.value)} className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => { onSelect(o.value); onClose(); }}
            className={`flex-1 min-w-0 ${ROW_H} px-3 rounded-xl flex items-center gap-2 text-left
              text-[13px] md:text-xs font-bold cursor-pointer hover:bg-neutral-100 ${MOTION}
              ${o.value === value ? ACCENT_TEXT : 'text-neutral-700'}`}
          >
            {o.value === value && <Check size={15} className="shrink-0" />}
            <span className="truncate">{o.label}</span>
          </button>
          {onDelete && options.length > 1 && (
            <button
              type="button"
              onClick={() => onDelete(o.value)}
              aria-label={`Delete ${o.label}`}
              className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-neutral-300
                hover:text-red-500 hover:bg-neutral-100 cursor-pointer"
            >
              <X size={16} />
            </button>
          )}
        </div>
      ))}
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick(); onClose(); }}
          className={`w-full ${ROW_H} px-3 mt-0.5 rounded-xl flex items-center gap-2 text-left
            text-[13px] md:text-xs font-bold ${ACCENT_TEXT} cursor-pointer hover:bg-[#93CB55]/15 ${MOTION}`}
        >
          {action.icon}
          <span className="truncate">{action.label}</span>
        </button>
      )}
    </div>
  );
}
